import React from "react";
import { Search, ChevronDown, LogOut, User as UserIcon, Building2, Command as CommandIcon, Bell } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { useBrand } from "@/lib/brand.jsx";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Topbar({ onOpenPalette }) {
  const { user, logout } = useAuth();
  const { brands, setActive, activeBrand, canSelectAll } = useBrand();
  const [notifications, setNotifications] = React.useState([]);

  React.useEffect(() => {
    api.get("/notifications").then((r) => setNotifications(r.data || [])).catch(() => {});
  }, []);

  const unreadCount = notifications.filter((item) => !item.read).length;
  const markNotificationsRead = async () => {
    const unread = notifications.filter((item) => !item.read).map((item) => item.id);
    if (!unread.length) return;
    try {
      await api.post("/notifications/read", { notification_ids: unread });
      setNotifications((items) => items.map((item) => ({ ...item, read: true })));
    } catch {
      // ignore toast noise in passive topbar refresh
    }
  };

  return (
    <header className="sticky top-0 z-30 glass" data-testid="topbar">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10 h-16 flex items-center gap-4">
        {/* Brand switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="brand-switcher"
              className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg border border-border bg-white hover:bg-[#f8f6f2] transition-colors text-sm shadow-sm"
            >
              <Building2 className="w-4 h-4 text-bronze" />
              <span className="font-medium text-slate-900">{activeBrand ? activeBrand.name : (canSelectAll ? "All Brands" : "Assigned Brand")}</span>
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Switch brand</DropdownMenuLabel>
            {canSelectAll ? (
              <>
                <DropdownMenuItem onClick={() => setActive("all")} data-testid="brand-option-all">
                  <Building2 className="w-4 h-4 mr-2 text-bronze" /> All brands
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            ) : null}
            {brands.map((b) => (
              <DropdownMenuItem key={b.id} onClick={() => setActive(b.id)} data-testid={`brand-option-${b.code}`}>
                <span className="w-6 h-6 rounded-md bg-[#f8f6f2] border border-border flex items-center justify-center text-[10px] font-mono mr-2 text-bronze">{b.code}</span>
                <span>{b.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Command */}
        <button
          onClick={onOpenPalette}
          data-testid="command-trigger"
          className="hidden md:flex flex-1 max-w-md items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-white hover:bg-[#f8f6f2] text-sm text-muted-foreground shadow-sm"
        >
          <Search className="w-4 h-4" />
          <span>Search leads, clients, invoices…</span>
          <kbd className="ml-auto px-1.5 py-0.5 rounded bg-[#f6f3ed] border border-border text-[10px] font-mono">⌘K</kbd>
        </button>

        <div className="ml-auto flex items-center gap-3">
          <DropdownMenu onOpenChange={(open) => { if (open) markNotificationsRead(); }}>
            <DropdownMenuTrigger asChild>
              <button className="relative flex items-center justify-center w-10 h-10 rounded-full border border-border bg-white hover:bg-[#f8f6f2] shadow-sm" data-testid="notifications-menu">
                <Bell className="w-4 h-4 text-slate-600" />
                {unreadCount > 0 ? (
                  <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-bronze text-black text-[10px] font-semibold flex items-center justify-center">
                    {unreadCount}
                  </span>
                ) : null}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Notifications</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{unreadCount} unread</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notifications.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground">No notifications yet.</div>
              ) : notifications.slice(0, 8).map((item) => (
                <div key={item.id} className={`px-3 py-3 border-b border-border last:border-b-0 ${item.read ? "bg-white" : "bg-[#fcf8ef]"}`}>
                  <div className="text-sm font-medium text-slate-900">{item.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">{item.message}</div>
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-full hover:bg-[#f8f6f2] transition">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#c5a059] to-[#7c6433] flex items-center justify-center text-black font-semibold text-sm">
                  {(user?.name || "?").slice(0, 1)}
                </div>
                <div className="hidden md:block leading-tight text-left">
                  <div className="text-xs font-medium text-slate-900">{user?.name}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{user?.role}</div>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-xs">{user?.email}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{user?.role}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem><UserIcon className="w-4 h-4 mr-2" /> Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={onOpenPalette}><CommandIcon className="w-4 h-4 mr-2" /> Command palette</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-rose-300 focus:text-rose-200">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
